// Docs: IMPLEMENTATION.md §3.2 (wake gate). Freshness thresholds and the
// backend wake-up handshake behind components/WakeGate.jsx.
//
// The api runs Serverless on Railway: after ~10 min without traffic the
// container is stopped and the next request cold-boots it (~5–10s). Any data
// the client holds from before that pause is just as old, so past the same
// threshold the app hides behind a wake screen until the backend answers and
// the visible queries have refetched, instead of painting stale numbers.
import { PERSIST_CACHE_KEY } from '../queryPersistence';

// Data older than this is not shown without refetching first. Matches the
// Railway Serverless sleep window: below it the server is almost certainly
// warm and the cached data at most a few minutes old.
export const STALE_AFTER_MS = 10 * 60_000;

// Wake-screen timeline (ms since the gate went up). Measured on Railway: a
// warm api answers /health in tens of ms server-side (one round trip for the
// client, well under a second); a cold boot takes ~5–10 s.
export const WAKE_THRESHOLDS = {
  // Nothing but the background for this long, so a warm server that answers
  // at once never flashes a loading screen.
  quiet: 400,
  // No /health answer by now means a cold start: say so, and start the game.
  sleeping: 1_500,
  // Longer than a normal cold start.
  slow: 25_000,
  // Stop waiting and offer retry / show-last-known-data.
  giveUp: 60_000,
};

// Once the backend answers, wait at most this long for the visible queries to
// refetch before revealing the app anyway.
export const SETTLE_CAP_MS = 12_000;
// ...and treat the query cache as settled after this long with no fetch in
// flight (covers lazy route chunks mounting their queries a beat later).
export const SETTLE_QUIET_MS = 300;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1';

// `/health` lives at the api root, not under /api/v1. It runs `SELECT 1`, so
// a 200 means the database is reachable too.
export const HEALTH_URL = `${API_BASE_URL.replace(/\/api\/v1\/?$/, '')}/health`;

// Newest `dataUpdatedAt` in the persisted query snapshot, or null when there
// is no usable snapshot (never saved, cleared on logout, unparsable).
export function persistedDataUpdatedAt() {
  try {
    const raw = window.localStorage.getItem(PERSIST_CACHE_KEY);
    if (!raw) return null;
    const queries = JSON.parse(raw)?.clientState?.queries || [];
    const newest = Math.max(0, ...queries.map((q) => q?.state?.dataUpdatedAt || 0));
    return newest > 0 ? newest : null;
  } catch {
    return null;
  }
}

export function isStale(updatedAt, now = Date.now()) {
  return updatedAt == null || now - updatedAt >= STALE_AFTER_MS;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function pingOnce(timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(HEALTH_URL, { cache: 'no-store', signal: controller.signal });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Per-attempt /health timeouts. Railway holds the FIRST request while a cold
// container boots (~5–10 s), so that one may wait long. Later attempts are
// short: during a redeploy a request can hang on the instance being replaced
// and never answer, and waiting 20 s on each such dead request kept the wake
// screen up long after the new backend answered in a fraction of a second.
export const PING_TIMEOUTS = { first: 20_000, retry: 6_000 };

// Resolves true once `/health` returns 2xx, false once `deadline` (epoch ms)
// passes or `signal` aborts. 5xx / network errors / timeouts (proxy hiccups
// mid-boot, hung requests mid-redeploy) are retried after a short pause.
export async function waitForBackend({ deadline, signal, timeouts = PING_TIMEOUTS }) {
  let attempt = 0;
  while (!signal?.aborted && Date.now() < deadline) {
    const remaining = deadline - Date.now();
    const timeout = attempt === 0 ? timeouts.first : timeouts.retry;
    attempt += 1;
    if (await pingOnce(Math.min(timeout, remaining))) return true;
    if (signal?.aborted) break;
    await sleep(Math.min(1_500, Math.max(0, deadline - Date.now())));
  }
  return false;
}

// Resolves once no query has been fetching for `quietMs`, or after `capMs`.
export function waitForQueriesIdle(client, { quietMs = SETTLE_QUIET_MS, capMs = SETTLE_CAP_MS } = {}) {
  return new Promise((resolve) => {
    let quietTimer = null;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(quietTimer);
      clearTimeout(capTimer);
      unsubscribe();
      resolve();
    };
    const check = () => {
      if (client.isFetching() === 0) {
        if (!quietTimer) quietTimer = setTimeout(finish, quietMs);
      } else if (quietTimer) {
        clearTimeout(quietTimer);
        quietTimer = null;
      }
    };
    const unsubscribe = client.getQueryCache().subscribe(check);
    const capTimer = setTimeout(finish, capMs);
    check();
  });
}
